import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/personalized-plans/admin/supplements`;

const MOMENTS = [
  { v: 'en_ayunas', l: '🌅 En ayunas' },
  { v: 'pre_entreno', l: '💪 Pre-entreno' },
  { v: 'intra_entreno', l: '⚡ Intra-entreno' },
  { v: 'antes_dormir', l: '🌙 Antes de dormir' },
  { v: 'con_comida_1', l: '🍽️ Con comida 1' },
  { v: 'con_comida_2', l: '🍽️ Con comida 2' },
  { v: 'con_comida_3', l: '🍽️ Con comida 3' },
  { v: 'con_comida_4', l: '🍽️ Con comida 4' },
  { v: 'con_comida_5', l: '🍽️ Con comida 5' },
];
const LEVELS = [
  { v: 'any', l: 'Todos' },
  { v: 'principiante', l: 'Principiante' },
  { v: 'intermedio', l: 'Intermedio' },
  { v: 'avanzado', l: 'Avanzado' },
];
const GOALS = [
  { v: 'any', l: 'Todos' },
  { v: 'perder_grasa', l: 'Perder grasa' },
  { v: 'ganar_musculo', l: 'Ganar músculo' },
  { v: 'definicion', l: 'Definición' },
  { v: 'recomposicion', l: 'Recomposición' },
  { v: 'mantener', l: 'Mantener' },
  { v: 'competencia', l: 'Competencia' },
];

const emptyForm = {
  name: '', dosage: '', instructions: '',
  levels: ['any'], goals: ['any'], moment: 'pre_entreno',
  allergens: [], timing: '', contraindications: '',
  active: true, sort_order: 0, notes: '',
};

const ALLERGENS = [
  { v: 'lacteos', l: '🥛 Lácteos' },
  { v: 'gluten', l: '🌾 Gluten' },
  { v: 'huevo', l: '🥚 Huevo' },
  { v: 'mariscos', l: '🦐 Mariscos' },
  { v: 'pescado', l: '🐟 Pescado' },
  { v: 'frutos_secos', l: '🥜 Frutos secos' },
  { v: 'mani', l: '🥜 Maní' },
  { v: 'soya', l: '🌱 Soya' },
];

const authHeaders = () => {
  const t = localStorage.getItem('deisycfit_admin_token') || localStorage.getItem('deisycfit_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function SupplementsAdminManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API, { headers: authHeaders() });
      setItems(res.data?.items || []);
    } catch {
      toast.error('No se pudieron cargar los suplementos');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Nombre requerido'); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/${editingId}`, form, { headers: authHeaders() });
        toast.success('Suplemento actualizado');
      } else {
        await axios.post(API, form, { headers: authHeaders() });
        toast.success('Suplemento creado');
      }
      setForm(emptyForm);
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    }
  };

  const startEdit = (item) => {
    setForm({
      name: item.name || '',
      dosage: item.dosage || '',
      instructions: item.instructions || '',
      // Compat: si el doc viene con singular legacy, lo convertimos a lista.
      levels: item.levels && item.levels.length ? item.levels : (item.level ? [item.level] : ['any']),
      goals: item.goals && item.goals.length ? item.goals : (item.goal ? [item.goal] : ['any']),
      moment: item.moment,
      allergens: item.allergens || [],
      timing: item.timing || '',
      contraindications: item.contraindications || '',
      active: !!item.active,
      sort_order: item.sort_order || 0,
      notes: item.notes || '',
    });
    setEditingId(item.id);
  };

  const toggleInArray = (arr, value) => (
    arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]
  );

  // Al elegir "any" limpia todo lo demás; al elegir un nivel específico saca "any".
  const toggleLevelOrGoal = (key, value) => {
    setForm(prev => {
      const current = prev[key] || [];
      let next;
      if (value === 'any') next = ['any'];
      else next = toggleInArray(current.filter(v => v !== 'any'), value);
      if (next.length === 0) next = ['any'];
      return { ...prev, [key]: next };
    });
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar esta recomendación?')) return;
    try {
      await axios.delete(`${API}/${id}`, { headers: authHeaders() });
      toast.success('Eliminado');
      await load();
    } catch { toast.error('No se pudo eliminar'); }
  };

  const toggle = async (item) => {
    try {
      await axios.put(`${API}/${item.id}`, { ...item, active: !item.active }, { headers: authHeaders() });
      await load();
    } catch { toast.error('No se pudo cambiar'); }
  };

  const grouped = MOMENTS.map(m => ({
    ...m,
    items: items.filter(i => i.moment === m.v),
  }));

  const inputCls = 'w-full px-3 py-2 rounded-lg bg-black/40 border border-white/15 text-white text-sm placeholder-white/40 focus:border-pink-400 focus:outline-none';
  const selectCls = inputCls + ' cursor-pointer';

  return (
    <Card className="border-pink-500/30" style={{ backgroundColor: 'rgba(236,72,153,0.04)' }}>
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          💊 Recomendaciones de suplementos
        </CardTitle>
        <p className="text-xs text-white/60">
          Crea, edita, activa o desactiva recomendaciones por combinación de nivel + objetivo + momento del día.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Formulario */}
        <div className="p-3 rounded-xl bg-black/40 border border-white/10 space-y-2" data-testid="supp-admin-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input placeholder="Nombre (ej: Creatina)" className={inputCls} value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })} data-testid="supp-admin-name" />
            <input placeholder="Dosis (ej: 5 g)" className={inputCls} value={form.dosage}
              onChange={e => setForm({ ...form, dosage: e.target.value })} data-testid="supp-admin-dosage" />
          </div>
          <input placeholder="Instrucciones (ej: Con 300 ml de agua)" className={inputCls}
            value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })}
            data-testid="supp-admin-instructions" />
          <input placeholder="⏱️ Timing preciso (ej: 30 min antes del entreno)" className={inputCls}
            value={form.timing} onChange={e => setForm({ ...form, timing: e.target.value })}
            data-testid="supp-admin-timing" />
          <input placeholder="🚨 Contraindicaciones (ej: no usar si estás embarazada)" className={inputCls}
            value={form.contraindications} onChange={e => setForm({ ...form, contraindications: e.target.value })}
            data-testid="supp-admin-contraindications" />
          <div>
            <p className="text-[11px] uppercase tracking-widest text-white/60 font-bold mb-1">Niveles</p>
            <div className="flex flex-wrap gap-1.5">
              {LEVELS.map(o => {
                const on = form.levels.includes(o.v);
                return (
                  <button key={o.v} type="button" onClick={() => toggleLevelOrGoal('levels', o.v)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-all ${on ? 'bg-pink-500/25 border-pink-400 text-white' : 'bg-white/5 border-white/15 text-white/70 hover:border-white/30'}`}
                    data-testid={`supp-admin-level-${o.v}`}>
                    {o.l}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-white/60 font-bold mb-1">Objetivos</p>
            <div className="flex flex-wrap gap-1.5">
              {GOALS.map(o => {
                const on = form.goals.includes(o.v);
                return (
                  <button key={o.v} type="button" onClick={() => toggleLevelOrGoal('goals', o.v)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-all ${on ? 'bg-pink-500/25 border-pink-400 text-white' : 'bg-white/5 border-white/15 text-white/70 hover:border-white/30'}`}
                    data-testid={`supp-admin-goal-${o.v}`}>
                    {o.l}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-white/60 font-bold mb-1">Alérgenos que contiene</p>
            <div className="flex flex-wrap gap-1.5">
              {ALLERGENS.map(o => {
                const on = (form.allergens || []).includes(o.v);
                return (
                  <button key={o.v} type="button"
                    onClick={() => setForm(p => ({ ...p, allergens: toggleInArray(p.allergens || [], o.v) }))}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-all ${on ? 'bg-amber-500/25 border-amber-400 text-white' : 'bg-white/5 border-white/15 text-white/70 hover:border-white/30'}`}
                    data-testid={`supp-admin-allergen-${o.v}`}>
                    {o.l}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-white/50 mt-1">Si la clienta tiene una de estas alergias, no verá este suplemento.</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-white/60 font-bold mb-1">Momento del día</p>
            <select className={selectCls} value={form.moment} onChange={e => setForm({ ...form, moment: e.target.value })} data-testid="supp-admin-moment">
              {MOMENTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-between gap-3">
            <label className="text-white/80 text-sm flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-pink-500" data-testid="supp-admin-active" />
              Activo
            </label>
            <div className="flex gap-2">
              {editingId && (
                <Button size="sm" variant="outline" onClick={() => { setForm(emptyForm); setEditingId(null); }}>
                  Cancelar
                </Button>
              )}
              <Button size="sm" onClick={submit} className="bg-pink-500 hover:bg-pink-600" data-testid="supp-admin-save">
                {editingId ? 'Guardar cambios' : '+ Crear suplemento'}
              </Button>
            </div>
          </div>
        </div>

        {/* Listado agrupado por momento */}
        {loading ? (
          <p className="text-white/60 text-sm">Cargando…</p>
        ) : (
          grouped.map(g => (
            <div key={g.v} className="rounded-xl bg-black/25 border border-white/10 p-3" data-testid={`supp-admin-group-${g.v}`}>
              <p className="text-[11px] uppercase tracking-widest text-pink-300 font-bold mb-2">{g.l} · {g.items.length}</p>
              {g.items.length === 0 ? (
                <p className="text-white/50 text-xs">Sin recomendaciones activas.</p>
              ) : (
                <ul className="space-y-1.5">
                  {g.items.map(it => (
                    <li key={it.id} className="flex items-start justify-between gap-2 p-2 rounded bg-white/5 border border-white/5"
                        data-testid={`supp-admin-item-${it.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate">
                          {it.name} {it.dosage && <span className="text-pink-200 text-xs">· {it.dosage}</span>}
                        </p>
                        <p className="text-[11px] text-white/60">
                          Nivel: <strong className="text-white/80">{(it.levels || [it.level]).filter(Boolean).join(', ') || 'any'}</strong>
                          {' · '}Meta: <strong className="text-white/80">{(it.goals || [it.goal]).filter(Boolean).join(', ') || 'any'}</strong>
                          {!it.active && <span className="ml-2 text-red-300">· Inactivo</span>}
                        </p>
                        {it.timing && <p className="text-[11px] text-cyan-200 mt-0.5">⏱️ {it.timing}</p>}
                        {it.allergens?.length > 0 && (
                          <p className="text-[11px] text-amber-200 mt-0.5">🚫 Contiene: {it.allergens.join(', ')}</p>
                        )}
                        {it.contraindications && (
                          <p className="text-[11px] text-red-200 mt-0.5">🚨 {it.contraindications}</p>
                        )}
                        {it.instructions && <p className="text-xs text-white/70 mt-1">{it.instructions}</p>}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => startEdit(it)} data-testid={`supp-admin-edit-${it.id}`}>Editar</Button>
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => toggle(it)}>
                          {it.active ? 'Pausar' : 'Activar'}
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs text-red-400 border-red-400/40" onClick={() => remove(it.id)} data-testid={`supp-admin-delete-${it.id}`}>
                          Borrar
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
